import { env } from '../utils/env';
import { hydrateCognitoSession, type AuthResult } from './cognito';

export type SsoProvider = 'google' | 'microsoft' | 'facebook' | 'apple' | 'linkedin';

export type SsoSessionResult =
  | {
      status: 'signed_in';
      tokens: {
        id_token: string;
        access_token: string;
        refresh_token: string;
        expires_in?: number;
      };
      user?: { id?: string; email?: string };
      return_to?: string;
      placement?: string;
      organisation_name?: string;
    }
  | {
      status: 'link_required';
      email: string;
      pending_link_id: string;
      provider?: string;
    }
  | {
      status: 'signup_required';
      email: string;
      prefill?: Record<string, unknown>;
      provider?: string;
    };

type NavigateFn = (to: string, options?: { state?: unknown; replace?: boolean }) => void;

type SsoDeps = {
  postSession?: (body: Record<string, unknown>) => Promise<SsoSessionResult>;
  fetchProof?: (provider: SsoProvider) => Promise<GoogleProof>;
  hydrate?: (idToken: string, accessToken: string, refreshToken: string) => AuthResult;
  navigate?: NavigateFn;
  onSignedIn?: (result: AuthResult, outcome: Extract<SsoSessionResult, { status: 'signed_in' }>) => void;
};

export type GoogleProof = { id_token?: string; code?: string };

type GoogleCodeClient = {
  requestCode: () => void;
};

declare global {
  interface Window {
    google?: {
      accounts?: {
        oauth2?: {
          initCodeClient: (config: Record<string, unknown>) => GoogleCodeClient;
        };
      };
    };
  }
}

const GSI_SRC = 'https://accounts.google.com/gsi/client';

function flagEnabled(raw: string): boolean {
  const flag = String(raw || '').toLowerCase();
  return flag === 'true' || flag === '1' || flag === 'yes';
}

export function isSsoAppsideEnabled(provider: SsoProvider): boolean {
  if (!flagEnabled(env.LUMINA_SSO_APPSIDE)) {
    return false;
  }
  const allowed = (env.LUMINA_SSO_PROVIDERS || 'google')
    .split(',')
    .map((part) => part.trim().toLowerCase())
    .filter(Boolean);
  return allowed.includes(provider);
}

export async function loadGsiScript(): Promise<void> {
  if (typeof window === 'undefined') {
    throw new Error('Google sign-in is only available in the browser');
  }
  if (window.google?.accounts) {
    return;
  }
  await new Promise<void>((resolve, reject) => {
    const existing = document.querySelector(`script[src="${GSI_SRC}"]`);
    if (existing) {
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', () =>
        reject(new Error('Failed to load Google Identity Services'))
      );
      if (window.google?.accounts) resolve();
      return;
    }
    const script = document.createElement('script');
    script.src = GSI_SRC;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load Google Identity Services'));
    document.head.appendChild(script);
  });
}

function googleFailureMessage(reason?: string): string {
  if (reason === 'popup_closed' || reason === 'access_denied') {
    return 'Google sign-in was cancelled.';
  }
  return 'Google sign-in did not complete. Try again.';
}

export async function fetchGoogleProof(): Promise<GoogleProof> {
  const clientId = env.GOOGLE_CLIENT_ID || '';
  if (!clientId) {
    throw new Error(
      'Google sign-in is not configured. Set VITE_GOOGLE_CLIENT_ID to the OAuth web client id.'
    );
  }
  await loadGsiScript();
  const initCodeClient = window.google?.accounts?.oauth2?.initCodeClient;
  if (!initCodeClient) {
    throw new Error('Google Identity Services is not available');
  }
  return new Promise((resolve, reject) => {
    let settled = false;
    const finish = (fn: () => void) => {
      if (settled) return;
      settled = true;
      fn();
    };
    const client = initCodeClient({
      client_id: clientId,
      scope: 'openid email profile',
      ux_mode: 'popup',
      callback: (resp: { code?: string; error?: string }) => {
        if (resp?.code) {
          finish(() => resolve({ code: resp.code }));
          return;
        }
        finish(() => reject(new Error(googleFailureMessage(resp?.error))));
      },
      error_callback: (err: { type?: string }) => {
        finish(() => reject(new Error(googleFailureMessage(err?.type))));
      },
    });
    client.requestCode();
  });
}

const providerProofLoaders: Record<SsoProvider, () => Promise<GoogleProof>> = {
  google: fetchGoogleProof,
  microsoft: () => Promise.reject(new Error('Microsoft SSO is not enabled yet')),
  facebook: () => Promise.reject(new Error('Facebook SSO is not enabled yet')),
  apple: () => Promise.reject(new Error('Apple SSO is not enabled yet')),
  linkedin: () => Promise.reject(new Error('LinkedIn SSO is not enabled yet')),
};

async function defaultPostSession(
  body: Record<string, unknown>
): Promise<SsoSessionResult> {
  const response = await fetch(`${env.API_ENDPOINT}/portal/auth/sso/session`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(env.API_KEY ? { 'x-api-key': env.API_KEY } : {}),
    },
    body: JSON.stringify(body),
  });
  const data = (await response.json().catch(() => ({}))) as Record<string, unknown>;
  if (!response.ok) {
    const message =
      (typeof data.message === 'string' && data.message) ||
      (typeof data.error === 'string' && data.error) ||
      'SSO sign-in did not complete. Try again.';
    throw new Error(message);
  }
  return data as unknown as SsoSessionResult;
}

export async function completeSsoLink(body: {
  pending_link_id: string;
  provider: string;
  email: string;
  password: string;
}): Promise<SsoSessionResult> {
  const response = await fetch(`${env.API_ENDPOINT}/portal/auth/sso/complete-link`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(env.API_KEY ? { 'x-api-key': env.API_KEY } : {}),
    },
    body: JSON.stringify(body),
  });
  const data = (await response.json().catch(() => ({}))) as Record<string, unknown>;
  if (!response.ok) {
    const message =
      (typeof data.message === 'string' && data.message) ||
      'Could not link this identity to the account.';
    throw new Error(message);
  }
  return data as unknown as SsoSessionResult;
}

export async function startSsoLogin(
  provider: SsoProvider,
  returnTo?: string,
  deps: SsoDeps = {}
): Promise<void> {
  const fetchProof = deps.fetchProof || providerProofLoaders[provider];
  if (!fetchProof) {
    throw new Error(`SSO provider '${provider}' is not supported`);
  }
  const proof = await fetchProof(provider);
  const postSession = deps.postSession || defaultPostSession;
  const result = await postSession({
    provider,
    proof,
    return_to: returnTo,
  });
  await dispatchSsoOutcome(provider, result, returnTo, deps);
}

export async function dispatchSsoOutcome(
  provider: SsoProvider,
  result: SsoSessionResult,
  returnTo?: string,
  deps: SsoDeps = {}
): Promise<void> {
  const navigate = deps.navigate;
  if (result.status === 'signed_in') {
    const hydrate = deps.hydrate || hydrateCognitoSession;
    const session = hydrate(
      result.tokens.id_token,
      result.tokens.access_token,
      result.tokens.refresh_token
    );
    deps.onSignedIn?.(session, result);
    const dest = result.return_to || returnTo || '/agents';
    const joined =
      (result.placement === 'auto_join' || result.placement === 'invite') &&
      session.user.organizationId &&
      dest.split('?')[0] === '/agents';
    navigate?.(
      joined
        ? `/agents?onboarding=member&joined=${encodeURIComponent(session.user.organizationId!)}`
        : dest,
      { replace: true }
    );
    return;
  }
  if (result.status === 'link_required') {
    navigate?.('/auth/link', {
      state: {
        email: result.email,
        pending_link_id: result.pending_link_id,
        provider: result.provider || provider,
      },
    });
    return;
  }
  if (result.status === 'signup_required') {
    const params = new URLSearchParams({ view: 'signUp' });
    if (returnTo) params.set('returnTo', returnTo);
    navigate?.(`/auth?${params.toString()}`, {
      state: {
        email: result.email,
        prefill: result.prefill,
        provider: result.provider || provider,
        notice:
          'Google confirmed this email. Add a display name and password to create your Luminafactory account.',
      },
    });
    return;
  }
  throw new Error('Unexpected SSO outcome');
}
